import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs, CircularProgress, Link, Typography, Box, Alert, Button } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchMachine,
  updateMachine,
  clearCurrentMachine,
} from '../../store/slices/machinesSlice';
import { MachineForm, MachineFormValues } from '../../components/machines/MachineForm';

export const MachineEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentMachine, isLoading, isUpdating, error } = useAppSelector((state) => state.machines);

  const shouldFetchMachine = !currentMachine || (id ? currentMachine.id !== id : false);

  useEffect(() => {
    if (!id) {
      return;
    }

    if (shouldFetchMachine) {
      dispatch(fetchMachine(id));
    }
  }, [dispatch, id, shouldFetchMachine]);

  useEffect(() => {
    return () => {
      dispatch(clearCurrentMachine());
    };
  }, [dispatch]);

  const initialValues = useMemo<MachineFormValues | null>(() => {
    if (!currentMachine) {
      return null;
    }

    return {
      name: currentMachine.name || '',
      description: currentMachine.description || '',
      location: currentMachine.location || '',
      status: currentMachine.status || 'active',
    };
  }, [currentMachine]);

  const handleCancel = () => {
    if (id) {
      navigate(`/machines/${id}`);
    } else {
      navigate('/machines');
    }
  };

  const handleSubmit = async (values: MachineFormValues) => {
    if (!id) {
      return;
    }

    try {
      const updated = await dispatch(updateMachine({ id, data: values })).unwrap();
      navigate(`/machines/${updated.id}`);
    } catch (submitError) {
      console.error('Failed to update machine:', submitError);
    }
  };

  if (!id) {
    return null;
  }

  if (!currentMachine) {
    if (!error) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Maschine konnte nicht geladen werden.'}
        </Alert>
        <Button variant="contained" onClick={handleCancel}>
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  if (!initialValues) {
    return null;
  }

  return (
    <>
      <Breadcrumbs sx={{ mb: 2, px: { xs: 2, md: 3 } }} separator="›">
        <Link
          color="inherit"
          href="/"
          onClick={(event) => {
            event.preventDefault();
            navigate('/');
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Startseite
        </Link>
        <Link
          color="inherit"
          href="/machines"
          onClick={(event) => {
            event.preventDefault();
            navigate('/machines');
          }}
        >
          Maschinen
        </Link>
        <Link
          color="inherit"
          href={`/machines/${id}`}
          onClick={(event) => {
            event.preventDefault();
            navigate(`/machines/${id}`);
          }}
        >
          {currentMachine?.name || 'Details'}
        </Link>
        <Typography color="text.primary">Bearbeiten</Typography>
      </Breadcrumbs>

      <MachineForm
        title={`Maschine bearbeiten`}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isUpdating}
        submitLabel="Änderungen speichern"
        error={error}
      />
    </>
  );
};
